import React from 'react';
import { Pagination, PaginationItem, PageLink, Link } from './StyledComponents.js';

/**
 * CommonPagination component for paginated navigation.
 * @param {Object} props
 * @param {number} props.page - Current page number (1-based)
 * @param {number} props.total - Total item count
 * @param {number} props.pageSize - Items per page
 * @param {string} props.query - Search query string
 * @param {object} props.location - Location object (for search params)
 * @param {function} props.fireCsEvent - Analytics event function
 * @param {string} [props.ariaLabel] - Aria label for nav
 * @param {function} [props.urlGenerator] - Function to generate pathname for a given page number
 * @param {string} [props.eventCategory] - Custom event category for analytics (optional)
 */
function CommonPagination({ page, total, pageSize, query, location, fireCsEvent, ariaLabel, urlGenerator, eventCategory = 'Ara Page' }) {
  const lastPage = Math.ceil(total / pageSize);
  if (lastPage <= 1) return null;

  // Default urlGenerator for backward compatibility
  const getPath = (targetPage) => {
    if (urlGenerator) return urlGenerator(targetPage, query);
    if (targetPage === 1) return `/ara/${query}`;
    return `/ara/${query}/${targetPage}`;
  };

  return (
    <nav aria-label={ariaLabel || 'Sayfalar'}>
      <Pagination className="pg-blue justify-content-center" style={{ gap: 6, padding: '8px 0' }}>
        {/* Previous button */}
        {page > 1 && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(page - 1), search: location.search }}
                rel="nofollow"
                className="page-link pagination-nav-btn"
                aria-label="Önceki"
                style={{ fontWeight: 500 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Prev' })}
              >
                ←
              </Link>
            </PageLink>
          </PaginationItem>
        )}
        {/* First page */}
        {page > 2 && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(1), search: location.search }}
                className="page-link"
                tabIndex="-1"
                style={{ borderRadius: 4 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Page link' })}
              >
                1
              </Link>
            </PageLink>
          </PaginationItem>
        )}
        {page > 3 && (
          <PaginationItem dataDisabled={true}>
            <PageLink className="page-link" tabIndex="-1" style={{ pointerEvents: 'none', color: '#aaa' }}>...</PageLink>
          </PaginationItem>
        )}
        {/* Previous page */}
        {page > 1 && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(page - 1), search: location.search }}
                rel="nofollow"
                className="page-link"
                style={{ borderRadius: 4 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Page link' })}
              >
                {page - 1}
              </Link>
            </PageLink>
          </PaginationItem>
        )}
        {/* Current page - highlighted */}
        <PaginationItem dataDisabled={true}>
          <PageLink
            className="page-link current-page"
            style={{ fontWeight: 700, background: '#e3f2fd', color: '#1976d2', borderRadius: 6, boxShadow: '0 1px 4px #e3f2fd' }}
          >
            {page}
          </PageLink>
        </PaginationItem>
        {/* Next page */}
        {page < lastPage && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(page + 1), search: location.search }}
                rel="nofollow"
                className="page-link"
                style={{ borderRadius: 4 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Page link' })}
              >
                {page + 1}
              </Link>
            </PageLink>
          </PaginationItem>
        )}
        {page + 2 < lastPage && (
          <PaginationItem dataDisabled={true}>
            <PageLink className="page-link" tabIndex="-1" style={{ pointerEvents: 'none', color: '#aaa' }}>...</PageLink>
          </PaginationItem>
        )}
        {/* Last page */}
        {page + 1 < lastPage && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(lastPage), search: location.search }}
                rel="nofollow"
                className="page-link"
                style={{ borderRadius: 4 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Page link' })}
              >
                {lastPage}
              </Link>
            </PageLink>
          </PaginationItem>
        )}
        {/* Next button */}
        {page < lastPage && (
          <PaginationItem>
            <PageLink>
              <Link
                to={{ pathname: getPath(page + 1), search: location.search }}
                rel="nofollow"
                className="page-link pagination-nav-btn"
                aria-label="Sonraki"
                style={{ fontWeight: 500 }}
                onClick={() => fireCsEvent && fireCsEvent({ category: eventCategory, action: 'Navigation', label: 'Next' })}
              >
                →
              </Link>
            </PageLink>
          </PaginationItem>
        )}
      </Pagination>
      <style>{`
        .page-link {
          transition: background 0.15s, color 0.15s;
        }
        .page-link:not([aria-disabled]):hover {
          background: #bbdefb;
          color: #0d47a1;
          text-decoration: none;
        }
        .pagination-nav-btn {
          min-width: 32px;
          font-size: 1.1em;
        }
        .current-page {
          cursor: default;
        }
      `}</style>
    </nav>
  );
}

export default CommonPagination;
